<template>
  <v-menu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    min-width="auto"
    offset-y
    transition="scale-transition"
  >
    <template #activator="{ on }">
      <v-text-field
        ref="cmpTextField"
        v-bind="attrs"
        :clearable="clearable"
        data-text-date-picker
        :dense="dense"
        :disabled="disabled"
        :error-messages="errorMessages"
        :label="label"
        readonly
        :value="formatDisplayDate(value)"
        v-on="on"
        @blur="onBlur"
        @click:clear="
          $emit('input', null);
          resetDisplayValueOnClear();
        "
        @focus="$emit('focus')"
        @keydown.enter="on.click"
        @keydown.space="on.click"
      />
    </template>
    <v-date-picker
      ref="cmpDatePicker"
      v-model="localDate"
      :allowed-dates="allowedDates"
      color="primary"
      :data-test-trigger-id="attrs['data-test-id']"
      :max="localMax"
      :min="localMin"
      no-title
      @input="handleDatePickerInput"
      @keyup.enter.stop
    />
  </v-menu>
</template>

<script lang="ts">
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { PropType, nextTick, shallowRef, watchEffect } from 'vue';
import { formatAnyDate, parseDate } from '@/utils/helpers/dates';
import { VDatePicker, VTextField } from 'vuetify/lib';

@Component({
  props: {
    value: {
      type: null as unknown as PropType<Date | null>,
      validator: (value: Date | null): boolean => {
        return value === null || value instanceof Date;
      },
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    min: {
      type: Date,
      required: false,
    },
    max: {
      type: Date,
      required: false,
    },
    allowedDates: {
      type: Function as PropType<(date: Date) => boolean>,
      required: false,
    },
    errorMessages: {
      type: Array, // Vue2 -> Vue3: string | string[]
      required: false,
    },
    dense: {
      type: Boolean,
    },
    clearable: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
  },
  inheritAttrs: false,
  setup() {
    const isMenuOpen = shallowRef(false);
    const isJustClosed = shallowRef(false);
    const cmpTextField = shallowRef<null | InstanceType<typeof VTextField>>(null);
    const cmpDatePicker = shallowRef<null | InstanceType<typeof VDatePicker>>(null);

    // focus on the selected date on date picker open
    watchEffect(() => {
      if (!isMenuOpen.value) return;

      setTimeout(() => {
        const elDatePicker = cmpDatePicker.value?.$el;
        if (elDatePicker === undefined) return;
        (
          elDatePicker.querySelector<HTMLButtonElement>('.v-btn--active') ??
          elDatePicker.querySelector<HTMLButtonElement>('.v-btn--outlined')
        )?.focus();
      }, 100);
    });

    return { isMenuOpen, isJustClosed, cmpTextField, cmpDatePicker };
  },
})
export default class TextDatePicker extends VTextField {
  protected readonly value!: null | Date;
  protected readonly label!: string;
  protected readonly min?: Date;
  protected readonly max?: Date;
  protected readonly allowedDates?: (date: Date) => boolean;
  protected readonly errorMessages?: string | string[];
  protected readonly dense?: boolean;
  protected readonly clearable?: boolean;
  protected readonly disabled?: boolean;

  protected console = console;
  protected isMenuOpen!: boolean;
  protected cmpTextField!: null | InstanceType<typeof VTextField>;
  protected cmpDatePicker!: null | InstanceType<typeof VDatePicker>;
  protected localDate: string | null = null;

  // $attrs doesn't include class and style (https://v2.vuejs.org/v2/api/#vm-attrs)
  protected get attrs(): Record<string, unknown> {
    return {
      ...this.$attrs,
      class: this.$vnode.data?.staticClass,
      style: this.$vnode.data?.staticStyle,
    };
  }

  protected get localMin(): string {
    return this.min ? this.formatVuetifyDate(this.min) : '';
  }

  protected get localMax(): string {
    return this.max ? this.formatVuetifyDate(this.max) : '';
  }

  @Watch('value')
  protected onValueChange(value: Date | null): void {
    this.localDate = value === null ? null : this.formatVuetifyDate(value);
  }

  protected created(): void {
    this.onValueChange(this.value);
  }

  protected handleDatePickerInput(): void {
    this.isMenuOpen = false;
    this.$emit('input', parseDate(this.localDate as string));
    this.cmpTextField?.$el.querySelector<HTMLInputElement>('input')?.focus();
  }

  protected formatVuetifyDate(date: Date): string {
    return formatAnyDate(date, 'yyyy-MM-dd');
  }

  protected formatDisplayDate(date: Date | null): string {
    return date === null ? '–' : formatAnyDate(date, 'MMM d, y');
  }

  protected async resetDisplayValueOnClear(): Promise<void> {
    // v-text-field clear internally does late update to null
    await nextTick();
    await nextTick();

    // now it's null, let's fix the value to the intended display
    if (this.cmpTextField && 'internalValue' in this.cmpTextField) {
      this.cmpTextField.internalValue = this.formatDisplayDate(this.value);
    }
  }

  protected hasParents(element: Element | null, parents: Element[]): boolean {
    while (element instanceof HTMLElement) {
      if (parents.includes(element)) return true;
      element = element.parentElement;
    }
    return false;
  }

  protected onBlur(): void {
    if (
      !this.hasParents(
        document.activeElement,
        [this.cmpTextField?.$el ?? null, this.cmpDatePicker?.$el ?? null].filter(
          Boolean
        ) as Element[]
      )
    ) {
      this.$emit('blur');
    }
  }
}
</script>

<style lang="scss" scoped></style>
